import * as React from 'react'

import { CheckIcon, ChevronDownIcon } from 'lucide-react'
import { observer } from 'mobx-react'
import { classList } from 'react-classlist-helper'
import { AutoSizer, List } from 'react-virtualized'

import { IPermitTypeField } from '~/client/graph/types.generated'
import IFormFilterFieldOption from '~/client/src/desktop/interfaces/IFormFilterFieldOption'
import PermitFieldIcon from '~/client/src/shared/components/PermitFieldIcon/PermitFieldIcon'
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '~/client/src/shared/components/shadcn-ui/popover'
import WorkflowStepNameLabel from '~/client/src/shared/components/WorkflowStepNameLabel'
import Localization from '~/client/src/shared/localization/LocalizationManager'

interface IProps {
  selectedField: IPermitTypeField
  isEditMode: boolean
  isInactive: boolean
  filterFieldsOptions: IFormFilterFieldOption[]

  onChange(fieldId: string): void
}

const MODAL_HEIGHT_RATIO = 0.4
const ROW_HEIGHT = 40
const OVERSCAN_ROW_COUNT = 10

@observer
export default class FilterFieldSelector extends React.Component<
  IProps,
  { isOpen: boolean }
> {
  public state = { isOpen: false }

  public render() {
    const { selectedField, isEditMode } = this.props

    return (
      <Popover open={this.state.isOpen} onOpenChange={this.setOpen}>
        <PopoverTrigger
          nativeButton={false}
          disabled={!isEditMode}
          render={
            <div
              className={classList({
                'my2 tw:flex tw:items-center tw:gap-[5px] conditional-field-tag no-flex h20 max-width250 ba-palette-brand-lighter brada2 px4': true,
                'outline-primary50-hover pointer': isEditMode,
              })}
            />
          }
        >
          {selectedField && <PermitFieldIcon fieldName={selectedField.type} />}
          <span className="text-ellipsis tw:flex-1" title={this.fieldCaption}>
            {this.fieldCaption}
          </span>
          {isEditMode && (
            <ChevronDownIcon className="tw:size-4 tw:shrink-0 tw:text-muted-foreground" />
          )}
        </PopoverTrigger>
        <PopoverContent
          side="bottom"
          align="start"
          className="tw:w-[500px] tw:overflow-hidden tw:p-0"
        >
          {this.fieldsList}
        </PopoverContent>
      </Popover>
    )
  }

  private get fieldsList(): JSX.Element {
    const { filterFieldsOptions } = this.props

    return (
      <div className="col no-outline-container">
        <span className="text large line-extra-large pt20 px24">
          {Localization.translator.selectField}
        </span>
        <div className="pt10 pb20 virtualized-list-smart-wrapper px24">
          {!filterFieldsOptions.length ? (
            <div className="text center large bold py20">
              {Localization.translator.none}
            </div>
          ) : (
            <AutoSizer disableHeight={true}>
              {({ width }) => (
                <List
                  data={filterFieldsOptions}
                  width={width}
                  height={Math.min(
                    window.innerHeight * MODAL_HEIGHT_RATIO,
                    ROW_HEIGHT * filterFieldsOptions.length,
                  )}
                  rowHeight={ROW_HEIGHT}
                  rowCount={filterFieldsOptions.length}
                  rowRenderer={this.renderOption}
                  scrollToAlignment="start"
                  overscanRowCount={OVERSCAN_ROW_COUNT}
                />
              )}
            </AutoSizer>
          )}
        </div>
      </div>
    )
  }

  private renderOption = ({ key, style, index }: any): JSX.Element => {
    const { filterFieldsOptions, selectedField } = this.props
    const option = filterFieldsOptions[index]

    if (!option) return null

    const { stepName, stepPosition, field, fieldPosition } = option
    const isSelected = !stepName && selectedField?.id === field?.id

    return (
      <div
        key={key}
        style={style}
        className={classList({
          'tw:flex tw:items-center tw:gap-2.5 bb-light-grey px10 text-ellipsis highlighted-hover pointer': true,
          'bg-blue-active': isSelected,
        })}
        onClick={() => {
          this.handleClick(stepName ? null : option.field.id)
          this.close()
        }}
      >
        {stepName ? (
          <WorkflowStepNameLabel
            stepPosition={stepPosition}
            stepName={stepName}
          />
        ) : (
          <>
            <CheckIcon
              className={classList({
                'tw:size-4 tw:shrink-0 tw:text-primary': true,
                'tw:invisible': !isSelected,
              })}
            />
            <span className="text large ellipsis tw:shrink-0">
              {[stepPosition, fieldPosition].filter(p => p).join('.')}
            </span>
            <PermitFieldIcon fieldName={field.type} />
            <span
              className="text large ellipsis mr5 tw:flex-1"
              title={field.caption}
            >
              {field.caption}
            </span>
          </>
        )}
      </div>
    )
  }

  private get fieldCaption(): string {
    const { selectedField, isInactive } = this.props

    if (!selectedField && isInactive) {
      return Localization.translator.deletedField
    }

    return selectedField
      ? selectedField.caption
      : Localization.translator.selectField
  }

  private setOpen = (isOpen: boolean) => this.setState({ isOpen })

  private close = () => this.setOpen(false)

  private handleClick(fieldId: string) {
    if (!fieldId) return

    const { onChange, selectedField } = this.props

    onChange(fieldId === selectedField?.id ? null : fieldId)
  }
}
